import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, FileSpreadsheet, ArrowLeft, Check, X } from 'lucide-react'
import Papa from 'papaparse'
import api from '../../lib/api'
import '../Pages.css'

const fieldOptions = [
  'Do not import',
  'Email',
  'Location',
  'Name',
  'Phone',
  'Linkedin',
  'Facebook',
  'Custom variable',
]

function AddContacts() {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [columns, setColumns] = useState([])
  const [uploadedRows, setUploadedRows] = useState([])
  const [fileName, setFileName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setFileName(file.name)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data
        setUploadedRows(data)

        if (data.length > 0) {
          const headers = Object.keys(data[0])
          const columnsWithSamples = headers.map((header) => ({
            name: header,
            samples: data.slice(0, 4).map((row) => (row[header] ? row[header].toString().trim() : '')),
            selectedType: 'Do not import',
          }))
          setColumns(columnsWithSamples)
        }
      },
      error: (err) => {
        console.error('CSV parsing error:', err)
      },
    })
  }

  const getUsedTypes = () =>
    columns
      .map((c) => c.selectedType)
      .filter((type) => type !== 'Do not import' && type !== 'Custom variable')

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const payload = {
        rows: uploadedRows,
        mapping: columns.map(({ name, selectedType }) => ({
          column: name,
          mappedTo: selectedType,
        })),
      }

      await api.post('/contacts/create-bulk', payload)
      navigate('/contacts')
    } catch (error) {
      console.error('Error submitting mapping:', error)
      alert('Error importing contacts. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReset = () => {
    setColumns([])
    setUploadedRows([])
    setFileName('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="page add-contacts-page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Add Contacts</h2>
          <p>Import contacts from a CSV file</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/contacts')}>
          <ArrowLeft size={18} /><span>Back to Contacts</span>
        </button>
      </div>

      {columns.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <div
            onClick={() => fileInputRef.current.click()}
            style={{
              border: '2px dashed var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '4rem 2rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.background = 'rgba(99, 102, 241, 0.03)' }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'transparent' }}
          >
            <div style={{ marginBottom: '1rem' }}>
              <Upload size={48} style={{ color: 'var(--color-primary)', opacity: 0.7 }} />
            </div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Import CSV</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
              Click to upload a CSV file with your contact data
            </p>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Supported format: .csv
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h3><FileSpreadsheet size={18} /> Map Columns - {fileName}</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span className="provider-badge">{uploadedRows.length} rows</span>
                <span className="provider-badge">{columns.length} columns</span>
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Column Name</th>
                    <th>Map To</th>
                    <th>Sample Data</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col, idx) => {
                    const usedTypes = getUsedTypes()
                    return (
                      <tr key={idx}>
                        <td style={{ fontWeight: 500 }}>{col.name}</td>
                        <td>
                          <select
                            value={col.selectedType}
                            onChange={(e) => {
                              const newCols = [...columns]
                              newCols[idx] = { ...newCols[idx], selectedType: e.target.value }
                              setColumns(newCols)
                            }}
                            style={{
                              padding: '0.375rem 0.75rem',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-bg-card)',
                              color: 'var(--color-text-primary)',
                              fontSize: '0.85rem',
                            }}
                          >
                            {fieldOptions.map((opt) => (
                              <option
                                key={opt}
                                value={opt}
                                disabled={
                                  usedTypes.includes(opt) &&
                                  col.selectedType !== opt &&
                                  opt !== 'Do not import' &&
                                  opt !== 'Custom variable'
                                }
                              >
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {col.samples?.map((s, i) => (
                              s && <span key={i} className="provider-badge">{s}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button className="btn btn-ghost" onClick={handleReset}>
              <X size={16} /><span>Cancel</span>
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
              <Check size={16} /><span>{submitting ? 'Importing...' : 'Import Contacts'}</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default AddContacts
